import React from 'react';
import { Switch, Route, RouteProps } from 'react-router-dom';

import { AppViews } from 'endpoints';

import { GuestGuard, AgencyGuard, AuthGuard } from 'guards';
import { FullPageLoader } from 'components/loaders';

import loadable from '@loadable/component';

import withTracker from 'utils/withTracker';

export type AppRoute = RouteProps & {
  guard?: React.ComponentType;
  routes?: AppRoute[];
  isAgency?: boolean;
  icon?: React.ComponentType;
  skipFooter?: boolean;
};

export const appRoutes: AppRoute[] = [
  {
    exact: true,
    path: AppViews.HOME,
    component: withTracker(loadable(() => import('screens/Home'))),
    guard: AgencyGuard,
  },
  {
    exact: true,
    path: AppViews.STOPS_INFO,
    component: loadable(() => import('components/StopsInformation/stops-information.component')),
  },
  { exact: true, path: AppViews.FAQS, component: withTracker(loadable(() => import('components/faqs'))) },
  { exact: true, path: AppViews.HELP, component: withTracker(loadable(() => import('components/static_pages/Help'))) },
  {
    exact: true,
    path: `${AppViews.STATIC_PAGE}/:slug`,
    component: withTracker(loadable(() => import('components/static_pages/Page'))),
  },

  {
    exact: true,
    path: AppViews.SEARCH,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('components/searchResult/SearchResult'))),
  },
  {
    exact: true,
    path: `${AppViews.CHECKOUT}/:id`,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('components/checkout/checkout.component'))),
  },
  {
    exact: true,
    path: `${AppViews.PAYMENT_LANDING_PAGE}/:id`,
    component: withTracker(loadable(() => import('components/checkout/landing_page'))),
  },

  {
    exact: true,
    path: AppViews.SIGN_IN,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/SignIn'))),
  },
  {
    exact: true,
    path: AppViews.SIGN_UP,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/SignUp'))),
  },
  {
    exact: true,
    path: AppViews.SIGN_IN_MOBILE,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/SignInMobile'))),
  },
  {
    exact: true,
    path: AppViews.CONFIRMATION,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/SignUp/Confirmation'))),
  },
  {
    exact: true,
    path: AppViews.SIGN_UP_COMPLETED,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/SignUp/SignUpCompleted'))),
  },
  {
    exact: true,
    path: AppViews.FORGOT_PASSWORD,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/ForgotPassword'))),
  },
  {
    exact: true,
    path: AppViews.MANAGE_YOUR_BOOKING,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/ManageYourBooking'))),
  },
  {
    exact: true,
    path: AppViews.BOOKINGS,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/users/bookings/bookingTickets'))),
  },
  {
    exact: true,
    path: AppViews.CANT_MANAGE,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('components/auth/CantManageBooking'))),
  },
  {
    exact: true,
    path: AppViews.ALTER_TRIP,
    guard: AuthGuard,
    component: withTracker(loadable(() => import('components/users/alterBooking'))),
  },
  {
    exact: true,
    path: `${AppViews.CHECKOUT_ALTER}/:id`,
    guard: AuthGuard,
    component: withTracker(loadable(() => import('components/checkout/checkout.component'))),
  },
  {
    exact: true,
    path: AppViews.USER_BOOKINGS,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('components/users/bookings'))),
  },
  {
    exact: true,
    path: `${AppViews.USER_BOOKINGS}/:bookingId`,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('components/users/bookings/bookingTickets'))),
  },
  {
    exact: true,
    path: AppViews.USER_PROFILE,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('components/users/Profile'))),
  },
  {
    exact: true,
    path: AppViews.USER_PAYMENT_METHODS,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('components/users/paymentMethods'))),
  },
  {
    exact: true,
    path: AppViews.USER_ADD_PAYMENT_METHODS,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('components/users/paymentMethods/AddPaymentMethod'))),
  },
  {
    exact: true,
    path: AppViews.AGENCY_LOGIN,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('modules/agency/components/Login'))),
  },
  {
    path: AppViews.AGENCY,
    guard: GuestGuard,
    component: withTracker(loadable(() => import('modules/agency'))),
  },
  {
    exact: false,
    path: AppViews.BOOKING_COUPON,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('modules/agency/screens/DetailsTicketsSearched'))),
  },
  {
    exact: true,
    path: AppViews.AGENCY_SETTINGS,
    guard: AgencyGuard,
    component: withTracker(loadable(() => import('modules/agency/screens/Settings'))),
    skipFooter: true,
  },
  {
    exact: true,
    path: AppViews.DETAIL_412,
    component: withTracker(loadable(() => import('components/detailErrors/detail412'))),
  },
  {
    exact: false,
    path: AppViews.MAPS,
    component: withTracker(loadable(() => import('components/localization'))),
  },
];

export const renderRoutes = (routes: AppRoute[]) =>
  routes.map((route) => {
    const Guard = route.guard || React.Fragment;
    const Component = route.component || React.Fragment;

    return (
      <Route
        key={`route-${route.path}`}
        path={route.path}
        exact={route.exact}
        render={(props) => (
          <Guard>
            {route.routes ? (
              renderRoutes(route.routes)
            ) : (
              <Component {...(route.path === AppViews.HELP && { key: props.location.key })} {...props} />
            )}
          </Guard>
        )}
      />
    );
  });

const Router = () => (
  <React.Suspense fallback={<FullPageLoader />}>
    <Switch>{renderRoutes(appRoutes)}</Switch>
  </React.Suspense>
);

export default Router;
